import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Plus, FileText, Edit3, Trash2, Eye, EyeOff, Home, BookOpen, LayoutList, Check, Settings, X, Twitter, Instagram, Linkedin, Github, BarChart2, Lock, Globe } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Input, Card } from '@brix/ui'
import { usePages, useCreatePage, useDeletePage, usePublishPage } from '@/hooks/usePages'
import { useApplyTemplate } from '@/hooks/useTemplates'
import { TemplatePicker } from '@/components/TemplatePicker'
import { ThemeEditor } from '@/components/ThemeEditor'
import { useSite, useUpdateSiteSettings, useUpdateSiteCustomDomain } from '@/hooks/useSites'
import { createPageSchema, type CreatePageDTO } from '@/application/dto'
import { ImageField } from '@/components/editor/fields'
import { useAuthStore } from '@/store/authStore'
import type { SiteSettings } from '@brix/types'

// ─── Create Page Modal ────────────────────────────────────────────────────────

function CreatePageModal({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const createPage = useCreatePage(siteId)
  const applyTemplate = useApplyTemplate(siteId)

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<CreatePageDTO>({
    resolver: zodResolver(createPageSchema),
  })

  const titleVal = watch('title', '')

  const onSubmit = async (data: CreatePageDTO) => {
    const page = await createPage.mutateAsync(data)
    if (selectedTemplate && page?.id) {
      await applyTemplate.mutateAsync({ pageId: page.id, templateId: selectedTemplate })
    }
    onClose()
  }

  const isPending = createPage.isPending || applyTemplate.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <Card padding="lg" className="w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="mb-6 text-lg font-bold text-gray-900">Create new page</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <Input
            label="Page title"
            placeholder="About us"
            {...register('title', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setValue('slug', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')),
            })}
            error={errors.title?.message}
          />
          <Input
            label="URL slug"
            placeholder="about-us"
            hint={titleVal ? `URL: /${watch('slug')}` : undefined}
            {...register('slug')}
            error={errors.slug?.message}
          />

          <hr className="border-gray-100" />

          <TemplatePicker selected={selectedTemplate} onSelect={setSelectedTemplate} />

          {(createPage.error ?? applyTemplate.error) && (
            <p className="text-sm text-red-600">{((createPage.error ?? applyTemplate.error) as Error).message}</p>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" loading={isPending} className="flex-1">
              {selectedTemplate ? 'Create with template' : 'Create page'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

// ─── Site Settings Panel ──────────────────────────────────────────────────────

function SiteSettingsPanel({ siteId }: { siteId: string }) {
  const { data: site } = useSite(siteId)
  const update = useUpdateSiteSettings()
  const updateDomain = useUpdateSiteCustomDomain()
  const tenant = useAuthStore((s) => s.tenant)
  const [settings, setSettings] = useState<SiteSettings | null>(null)
  const [saved, setSaved] = useState(false)
  const [customDomain, setCustomDomain] = useState<string | null>(null)
  const [domainSaved, setDomainSaved] = useState(false)

  useEffect(() => {
    if (site && customDomain === null) {
      setCustomDomain(site.customDomain ?? '')
    }
  }, [site, customDomain])

  const current = settings ?? site?.settings ?? {}

  const set = (patch: Partial<SiteSettings>) => {
    setSettings(prev => ({ ...(prev ?? site?.settings ?? {}), ...patch }))
    setSaved(false)
  }

  const setNavLink = (i: number, field: 'label' | 'href', value: string) => {
    const links = [...(current.navLinks ?? [])]
    const existing = links[i] ?? { label: '', href: '#' }
    links[i] = { label: existing.label ?? '', href: existing.href ?? '#', [field]: value }
    set({ navLinks: links })
  }

  const addNavLink = () => set({ navLinks: [...(current.navLinks ?? []), { label: '', href: '#' }] })

  const removeNavLink = (i: number) => {
    const links = [...(current.navLinks ?? [])]
    links.splice(i, 1)
    set({ navLinks: links })
  }

  const save = async () => {
    if (!settings) return
    await update.mutateAsync({ id: siteId, settings: current })
    setSaved(true)
  }

  if (!site) return <div className="flex justify-center py-20"><div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" /></div>

  const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500'

  return (
    <div className="max-w-xl space-y-8 pt-2">

      {/* Nav links */}
      <div>
        <h2 className="mb-1 text-sm font-semibold text-gray-900">Navigation</h2>
        <p className="mb-3 text-xs text-gray-400">Links shown in the menu. Use <code className="bg-gray-100 px-1 rounded">#id</code> to scroll to a section on the same page.</p>
        <div className="space-y-2">
          {(current.navLinks ?? []).map((link, i) => (
            <div key={`${i}-${link.href}`} className="flex items-center gap-2">
              <input
                aria-label="Link label"
                placeholder="Label (e.g. Services)"
                value={link.label}
                onChange={e => setNavLink(i, 'label', e.target.value)}
                className={`${inputCls} flex-1`}
              />
              <input
                aria-label="Link URL"
                placeholder="URL (e.g. #services)"
                value={link.href}
                onChange={e => setNavLink(i, 'href', e.target.value)}
                className={`${inputCls} flex-1`}
              />
              <button onClick={() => removeNavLink(i)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-4">
          <button
            onClick={addNavLink}
            className="flex items-center gap-1.5 text-xs text-violet-600 hover:text-violet-800 font-medium"
          >
            <Plus className="h-3.5 w-3.5" /> Add link
          </button>
          {!(current.navLinks ?? []).some(l => l.href === '/blog') && (
            <button
              onClick={() => set({ navLinks: [...(current.navLinks ?? []), { label: 'Blog', href: '/blog' }] })}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-violet-600 font-medium transition-colors"
            >
              <BookOpen className="h-3.5 w-3.5" /> Add Blog link
            </button>
          )}
        </div>
      </div>

      {/* Design: nav style, theme presets, primary color, typography, border radius */}
      <ThemeEditor value={current} onChange={set} />

      {/* Brand */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-gray-900">Brand</h2>
        <div className="space-y-3">
          <ImageField
            label="Logo"
            value={current.logo ?? ''}
            onChange={url => set({ logo: url || undefined })}
            folder="logos"
          />
          <div>
            <ImageField
              label="Favicon"
              value={current.favicon ?? ''}
              onChange={url => set({ favicon: url || undefined })}
              folder="favicons"
            />
            <p className="mt-1 text-xs text-gray-400">32×32 px .ico or .png recommended. Shown in the browser tab.</p>
          </div>
        </div>
      </div>

      {/* Social links */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-gray-900">Social links</h2>
        <div className="space-y-2">
          {([
            { key: 'twitter',   icon: Twitter,   placeholder: 'https://twitter.com/yourhandle' },
            { key: 'instagram', icon: Instagram, placeholder: 'https://instagram.com/yourhandle' },
            { key: 'linkedin',  icon: Linkedin,  placeholder: 'https://linkedin.com/company/your-company' },
            { key: 'github',    icon: Github,    placeholder: 'https://github.com/yourorg' },
          ] as const).map(({ key, icon: Icon, placeholder }) => (
            <div key={key} className="flex items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                <Icon className="h-4 w-4 text-gray-500" />
              </div>
              <input
                aria-label={`${key} URL`}
                placeholder={placeholder}
                value={current.socialLinks?.[key] ?? ''}
                onChange={e => set({ socialLinks: { ...(current.socialLinks ?? {}), [key]: e.target.value || undefined } })}
                className={`${inputCls} flex-1`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Analytics */}
      {import.meta.env['VITE_ANALYTICS_ENABLED'] === 'true' && (() => {
        const canAnalytics = (tenant?.plan ?? 'free') !== 'free'
        const trackingEnabled = current.trackPageViews !== false
        return (
          <div>
            <h2 className="mb-3 text-sm font-semibold text-gray-900">Analytics</h2>
            <div className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2">
                {canAnalytics ? (
                  <BarChart2 className="h-4 w-4 text-violet-500" />
                ) : (
                  <Lock className="h-4 w-4 text-gray-400" />
                )}
                <span className="text-sm text-gray-700">Page view tracking</span>
                {!canAnalytics && (
                  <Link to="/billing" className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-600 hover:bg-amber-100 transition-colors">
                    Starter+
                  </Link>
                )}
              </div>
              <button
                type="button"
                onClick={() => canAnalytics && set({ trackPageViews: !trackingEnabled })}
                className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus-visible:outline-none ${
                  canAnalytics ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'
                } ${trackingEnabled && canAnalytics ? 'bg-violet-600' : 'bg-gray-200'}`}
                aria-checked={trackingEnabled && canAnalytics}
                aria-label="Page view tracking"
                role="switch"
              >
                <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                  trackingEnabled && canAnalytics ? 'translate-x-4' : 'translate-x-0'
                }`} />
              </button>
            </div>
          </div>
        )
      })()}

      {/* Custom Domain */}
      <div>
        <h2 className="mb-1 text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Globe className="h-4 w-4 text-gray-400" /> Custom domain
        </h2>
        <p className="mb-3 text-xs text-gray-400">
          Point a CNAME record from your domain to <code className="bg-gray-100 px-1 rounded">{site.subdomain}.brix.app</code>, then enter it here.
        </p>
        <div className="flex items-center gap-2">
          <input
            aria-label="Custom domain"
            placeholder="e.g. www.yourdomain.com"
            value={customDomain ?? ''}
            onChange={e => { setCustomDomain(e.target.value); setDomainSaved(false) }}
            className={`${inputCls} flex-1`}
          />
          <Button
            onClick={async () => {
              await updateDomain.mutateAsync({ id: siteId, customDomain: customDomain?.trim() || null })
              setDomainSaved(true)
            }}
            loading={updateDomain.isPending}
            disabled={customDomain === null}
          >
            Save
          </Button>
        </div>
        {domainSaved && (
          <p className="mt-1.5 flex items-center gap-1 text-xs text-green-600">
            <Check className="h-3.5 w-3.5" /> Domain saved
          </p>
        )}
        {(customDomain?.trim()) && (
          <p className="mt-1.5 text-xs text-gray-400">
            Your site will be accessible at{' '}
            <a href={`https://${customDomain.trim()}`} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">
              {customDomain.trim()}
            </a>{' '}
            once DNS propagates.
          </p>
        )}
      </div>

      {/* Footer */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-gray-900">Footer</h2>
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-for -- htmlFor+id below already associates this label; the deprecated rule also wants nesting, not used here to keep the existing label/input layout */}
          <label htmlFor="footer-text" className="mb-1 block text-xs font-medium text-gray-600">Footer text</label>
          <input
            id="footer-text"
            aria-label="Footer text"
            placeholder={`© ${new Date().getFullYear()} ${site.name}`}
            value={current.footerText ?? ''}
            onChange={e => set({ footerText: e.target.value || undefined })}
            className={inputCls}
          />
          <p className="mt-1 text-xs text-gray-400">Copyright notice or tagline shown at the bottom of every page.</p>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={save} loading={update.isPending} disabled={!settings}>
          Save changes
        </Button>
        {saved && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <Check className="h-3.5 w-3.5" /> Saved
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Pages Page ───────────────────────────────────────────────────────────────

export function PagesPage() {
  const { siteId = '' } = useParams()
  const [showModal, setShowModal] = useState(false)
  const [activeTab, setActiveTab] = useState<'pages' | 'blog' | 'settings'>('pages')
  const { data: pages = [], isLoading } = usePages(siteId)
  const deletePage = useDeletePage(siteId)
  const publishPage = usePublishPage(siteId)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)

  const toggleSelectionMode = () => {
    setSelectionMode(m => !m)
    setSelected(new Set())
  }

  const toggleItem = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAll = () =>
    setSelected(selected.size === pages.length && pages.length > 0 ? new Set() : new Set(pages.map(p => p.id)))

  const handleBulkPublish = async (publish: boolean) => {
    setBulkLoading(true)
    await Promise.all([...selected].map(id => publishPage.mutateAsync({ pageId: id, publish })))
    setSelected(new Set())
    setBulkLoading(false)
  }

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selected.size} page${selected.size > 1 ? 's' : ''}? This cannot be undone.`)) return
    setBulkLoading(true)
    await Promise.all([...selected].map(id => deletePage.mutateAsync(id)))
    setSelected(new Set())
    setSelectionMode(false)
    setBulkLoading(false)
  }

  const tabCls = (t: typeof activeTab) =>
    `flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
      activeTab === t
        ? 'border-violet-600 text-violet-700'
        : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
    }`

  return (
    <div className="p-8">
      {/* Breadcrumb + sub-nav */}
      <div className="mb-6">
        <Link to="/sites" className="text-sm text-gray-400 hover:text-violet-600">← Sites</Link>
        <div className="mt-2 flex items-center gap-1 border-b border-gray-200 pb-0">
          <button onClick={() => setActiveTab('pages')} className={tabCls('pages')}>
            <LayoutList className="h-4 w-4" /> Pages
          </button>
          <Link
            to={`/sites/${siteId}/blog`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300 -mb-px transition-colors"
          >
            <BookOpen className="h-4 w-4" /> Blog posts
          </Link>
          <Link
            to={`/sites/${siteId}/analytics`}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-900 border-b-2 border-transparent hover:border-gray-300 -mb-px transition-colors"
          >
            <BarChart2 className="h-4 w-4" /> Analytics
          </Link>
          <button onClick={() => setActiveTab('settings')} className={tabCls('settings')}>
            <Settings className="h-4 w-4" /> Settings
          </button>
        </div>
      </div>

      {activeTab === 'settings' && <SiteSettingsPanel siteId={siteId} />}
      {activeTab !== 'settings' && (<>

      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Pages</h1>
        <div className="flex items-center gap-2">
          {pages.length > 0 && (
            <button
              onClick={toggleSelectionMode}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                selectionMode
                  ? 'border-violet-500 bg-violet-50 text-violet-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {selectionMode ? 'Cancel' : 'Select'}
            </button>
          )}
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4" />
            New page
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-600 border-t-transparent" />
        </div>
      ) : pages.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-20">
          <FileText className="mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm text-gray-500">No pages yet</p>
          <Button className="mt-4" onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4" />
            Create your first page
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Bulk action bar */}
          {selectionMode && selected.size > 0 && (
            <div className="flex items-center gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3">
              <span className="text-sm font-medium text-violet-700">
                {selected.size} selected
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => void handleBulkPublish(true)}
                  disabled={bulkLoading}
                  className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  <Eye className="h-3.5 w-3.5" /> Publish
                </button>
                <button
                  onClick={() => void handleBulkPublish(false)}
                  disabled={bulkLoading}
                  className="flex items-center gap-1.5 rounded-lg bg-gray-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  <EyeOff className="h-3.5 w-3.5" /> Unpublish
                </button>
                <button
                  onClick={() => void handleBulkDelete()}
                  disabled={bulkLoading}
                  className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          )}

          {/* Select-all row */}
          {selectionMode && (
            <div className="flex items-center gap-3 px-5 py-2">
              <input
                type="checkbox"
                aria-label={selected.size === pages.length && pages.length > 0 ? 'Deselect all' : 'Select all'}
                checked={selected.size === pages.length && pages.length > 0}
                onChange={toggleAll}
                className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600"
              />
              <span className="text-xs text-gray-500">
                {selected.size === pages.length && pages.length > 0 ? 'Deselect all' : 'Select all'}
              </span>
            </div>
          )}

          {pages.map((page) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click-anywhere-on-row is a mouse-only convenience; the checkbox inside is the keyboard-operable control for toggling selection
            <div
              key={page.id}
              onClick={selectionMode ? () => toggleItem(page.id) : undefined}
              className={`flex items-center gap-4 rounded-xl border bg-white px-5 py-4 ${
                selectionMode ? 'cursor-pointer' : ''
              } ${
                selectionMode && selected.has(page.id)
                  ? 'border-violet-300 bg-violet-50'
                  : 'border-gray-200'
              }`}
            >
              {selectionMode && (
                <input
                  type="checkbox"
                  aria-label={`Select ${page.title}`}
                  checked={selected.has(page.id)}
                  onChange={() => toggleItem(page.id)}
                  onClick={e => e.stopPropagation()}
                  className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600"
                />
              )}
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                {page.isHome ? <Home className="h-4 w-4 text-violet-600" /> : <FileText className="h-4 w-4 text-gray-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{page.title}</p>
                <p className="text-xs text-gray-400">/{page.slug}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                page.published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {page.published ? 'Published' : 'Draft'}
              </span>
              {!selectionMode && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- stopPropagation only, keeps the row's own onClick from firing; the Link/buttons inside remain independently keyboard-operable
                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                  <Link
                    to={`/editor/${page.id}`}
                    className="p-2 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                    title="Edit"
                  >
                    <Edit3 className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => publishPage.mutate({ pageId: page.id, publish: !page.published })}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                    title={page.published ? 'Unpublish' : 'Publish'}
                  >
                    {page.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => { if (confirm(`Delete "${page.title}"?`)) deletePage.mutate(page.id) }}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && <CreatePageModal siteId={siteId} onClose={() => setShowModal(false)} />}
      </>)}
    </div>
  )
}
